import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Container,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Business,
  Email,
  Language,
  Person,
  ArrowForward,
  VpnKey,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { APP_NAME, API_CONFIG } from '../helpers/constants';
import { createCompany, CompanySignupRequest } from '../services/companyService';
import { loginUser } from '../services/authService';
import { encryptPassword } from '@/utils/crypto';
import {
  validatePasswordRequirements,
  isPasswordValid,
  type PasswordValidation,
} from '../helpers/passwordUtils';
import PasswordPolicyIndicator from '../components/PasswordPolicyIndicator';
import PasswordMatchIndicator from '../components/PasswordMatchIndicator';


const CompanySignup: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const { isDarkMode } = useTheme();
  const { login, loginWithResponse } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: '',
    domain: '',
    contactEmail: '',
    adminFullName: '',
    password: '',
    confirmPassword: '',
  });
  
  const [formErrors, setFormErrors] = useState({
    companyName: '',
    domain: '',
    contactEmail: '',
    adminFullName: '',
    password: '',
    confirmPassword: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    minLength: false,
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  // Theme context
  const muiTheme = useMuiTheme();

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement> | { target: { value: unknown } }) => {
    const newValue = event.target.value as string;
    setFormData({
      ...formData,
      [field]: newValue,
    });
    
    // Clear error for this field when user starts typing
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors({
        ...formErrors,
        [field]: '',
      });
    }
    
    // Real-time domain validation when domain or email changes
    if (field === 'domain' || field === 'contactEmail') {
      const updatedFormData = { ...formData, [field]: newValue };
      const domain = updatedFormData.domain.trim().toLowerCase();
      const email = updatedFormData.contactEmail.trim();
      
      // Only validate if both domain and email are provided
      if (domain && email) {
        // Check if email format is valid first
        if (/\S+@\S+\.\S+/.test(email)) {
          const emailDomain = email.split('@')[1]?.toLowerCase().trim();
          if (emailDomain && emailDomain !== domain) {
            setFormErrors(prev => ({
              ...prev,
              contactEmail: `Email domain must match the entered domain. Expected: @${domain}`,
            }));
          } else if (emailDomain === domain) {
            // Clear error if domains match (but keep other email format errors if any)
            setFormErrors(prev => {
              const newErrors = { ...prev };
              // Only clear if the current error is about domain mismatch
              if (newErrors.contactEmail?.includes('domain must match')) {
                newErrors.contactEmail = '';
              }
              return newErrors;
            });
          }
        }
      } else {
        // If either field is empty, clear domain mismatch error
        setFormErrors(prev => {
          const newErrors = { ...prev };
          if (newErrors.contactEmail?.includes('domain must match')) {
            newErrors.contactEmail = '';
          }
          return newErrors;
        });
      }
    }

    // Update password validation and match state in real-time
    if (field === 'password') {
      setPasswordValidation(validatePasswordRequirements(newValue));
      if (formData.confirmPassword) {
        setPasswordsMatch(newValue === formData.confirmPassword);
      }
    } else if (field === 'confirmPassword') {
      setPasswordsMatch(formData.password === newValue);
    }
  };

  const validateForm = () => {
    const errors: any = {};
    
    // Enhanced company name validation
    if (!formData.companyName) {
      errors.companyName = 'Company name is required';
    } else {
      const companyName = formData.companyName.trim();
      if (companyName.length < 2) {
        errors.companyName = 'Company name must be at least 2 characters';
      } else if (companyName.length > 100) {
        errors.companyName = 'Company name must be less than 100 characters';
      } else {
        // Check for invalid characters
        const invalidChars = /[^a-zA-Z0-9\s\-&.'()]/;
        if (invalidChars.test(companyName)) {
          errors.companyName = 'Company name contains invalid characters. Please use only letters, numbers, spaces, hyphens, ampersands, periods, and apostrophes.';
        }
      }
    }
    
    if (!formData.domain) {
      errors.domain = 'Domain is required';
    } else {
      const domain = formData.domain.trim();
      // Basic domain validation
      const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.([a-zA-Z]{2,}|[a-zA-Z]{2,}\.[a-zA-Z]{2,})$/;
      if (!domainRegex.test(domain)) {
        errors.domain = 'Please enter a valid domain (e.g., example.com)';
      }
    }
    if (!formData.contactEmail) {
      errors.contactEmail = 'Contact email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.contactEmail)) {
      errors.contactEmail = 'Please enter a valid email address';
    } else {
      // Validate that email domain matches the entered domain
      const emailDomain = formData.contactEmail.split('@')[1]?.toLowerCase().trim();
      const enteredDomain = formData.domain.trim().toLowerCase();
      
      if (emailDomain && enteredDomain && emailDomain !== enteredDomain) {
        errors.contactEmail = `Email domain must match the entered domain. Expected: @${enteredDomain}`;
      }
    }
    
    
    if (!formData.adminFullName) {
      errors.adminFullName = 'Admin user fullname is required';
    } else {
      const adminFullName = formData.adminFullName.trim();
      if (adminFullName.length < 2) {
        errors.adminFullName = 'Admin user fullname must be at least 2 characters';
      } else if (adminFullName.length > 100) {
        errors.adminFullName = 'Admin user fullname must be less than 100 characters';
      } else {
        // Check for invalid characters
        const invalidChars = /[^a-zA-Z\s\-'.]/;
        if (invalidChars.test(adminFullName)) {
          errors.adminFullName = 'Admin user fullname contains invalid characters. Please use only letters, spaces, hyphens, periods, and apostrophes.';
        }
      }
    }
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (!isPasswordValid(passwordValidation)) {
      errors.password = 'Password must meet all requirements';
    }
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFormErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    return isValid;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const encryptedPassword = await encryptPassword(formData.password);

      // Prepare company creation data
      const companyData: CompanySignupRequest = {
        name: formData.companyName.trim(),
        domain: formData.domain.trim(),
        contactEmail: formData.contactEmail.trim(),
        status: 'active',
        planId: null, // Will be set during onboarding
        password: encryptedPassword,
        encrypted: true,
        userName: formData.adminFullName.trim(),
        platform_name: APP_NAME,
        platform_url: API_CONFIG.NODE_BASE_URL,
      };

      // Create company
      const companyResponse = await createCompany(companyData);
      
      if (companyResponse.success) {

        // Use the token from company creation directly
        const companyToken = companyResponse.data.token;
        const customerId = companyResponse.data.customerId;

        // Removed (previously deleted) user signing up again: the backend creates the
        // company but returns no token until the email owner verifies the address.
        // Do NOT log in; just show the "check your email" page.
        //
        // PR #677 review (M1) — this case requires a real customerId. A token-less response
        // with no customerId isn't the verification case, it's a broken response (e.g. a
        // backend bug or partial failure) — falling through below throws instead of silently
        // storing onboarding data / "companyId": "undefined" for a company that may not exist.
        //
        // PR #684 review (H2) — no login happens in this branch (no loginWithResponse call), so
        // there is no session to attach this data to. Writing it to localStorage/sessionStorage
        // anyway left it there indefinitely: on a shared machine, a different user who later logs
        // in would inherit this person's contactEmail/companyId and the "new signup" plan-badge
        // suppression. EmailVerificationSent only reads `state.email` below, and Onboarding.tsx
        // already synthesizes fresh onboarding data from the real logged-in user when none is
        // stored, so this branch doesn't need to write anything here at all — the real signup
        // session is established later, at verified login (EmailVerifiedSuccess.tsx).
        if (!companyToken && customerId) {
          if (companyResponse.message) {
            showSuccess(companyResponse.message);
          }
          navigate('/emailVerificationSent', {
            // PR #677 review (M2) — the backend discards the typed password for this case;
            // EmailVerificationSent shows a persistent notice to use Forgot password instead
            // of the owner only finding out when a later password login fails with E007.
            state: { email: formData.contactEmail.trim(), passwordNotSet: true },
          });
          return;
        }

        if (!companyToken) {
          throw new Error(companyResponse.message || 'No token received from company creation');
        }

        // Create a mock login response with the company token
        const mockLoginResponse = {
          access_token: companyToken,
          refresh_token: companyToken, // Use same token as refresh for now
          token_type: 'Bearer',
          expires_in: 3600,
          role: 'admin',
          email_id: formData.contactEmail.trim(),
          name: formData.companyName.trim(),
          user_id: customerId,
          company_id: customerId,
        };

        // Update AuthContext with the company token
        loginWithResponse(mockLoginResponse);

        // Store onboarding data for the onboarding flow
        const onboardingData = {
          customerId,
          companyName: companyResponse.data.companyName,
          contactEmail: formData.contactEmail.trim(),
          token: companyResponse.data.token,
          planId: companyResponse.data.planId,
          planName: companyResponse.data.planName,
          // Always true for brand-new signups — the backend may return false in some
          // environments even though the user has not yet consciously chosen a paid plan.
          isDefaultPlan: true,
          accessToken: companyToken,
          userId: customerId,
          companyId: customerId,
        };

        // Mark this as a fresh signup session so Onboarding can suppress the
        // "Current Plan" badge even if getMySubscription succeeds (auto-assigned free plan).
        sessionStorage.setItem('isNewSignup', 'true');

        // Store onboarding data
        localStorage.setItem('onboardingData', JSON.stringify(onboardingData));

        // Store additional company data
        localStorage.setItem('companyId', customerId);

        // Navigate to email verification sent page
        navigate('/emailVerificationSent', {
          state: { email: formData.contactEmail.trim() },
        });
      } else {
        throw new Error(companyResponse.message || 'Company creation failed');
      }
    } catch (error: any) {
      console.error('Error creating company:', error);

      // Show the exact error message from the API response as-is
      const errorMessage = error.message || 'Failed to create company';

      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        {/* Logo above the card */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box
            component="img"
            src={isDarkMode ? '/fulllogo-white.png' : '/fulllogo.png'}
            alt={APP_NAME}
            sx={{
              height: 48,
              width: 'auto',
              maxWidth: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>

        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            borderRadius: 2,
            backgroundColor: 'background.paper',
          }}
        >
          {/* Header inside card */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Welcome to {APP_NAME}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create your company account to get started
            </Typography>
          </Box>

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Company Name"
              value={formData.companyName}
              onChange={handleInputChange('companyName')}
              fullWidth
              placeholder="Acme Corporation"
              variant="outlined"
              error={!!formErrors.companyName}
              helperText={formErrors.companyName}
              required
              InputProps={{
                startAdornment: <Business sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            
            <TextField
              label="Domain"
              value={formData.domain}
              onChange={handleInputChange('domain')}
              fullWidth
              placeholder="acme.com"
              variant="outlined"
              error={!!formErrors.domain}
              helperText={formErrors.domain}
              required
              InputProps={{
                startAdornment: <Language sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            
            <TextField
              label="Contact Email"
              value={formData.contactEmail}
              onChange={handleInputChange('contactEmail')}
              fullWidth
              placeholder="john@acme.com"
              variant="outlined"
              type="email"
              error={!!formErrors.contactEmail}
              helperText={formErrors.contactEmail}
              required
              InputProps={{
                startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            
            <TextField
              label="Admin User Fullname"
              value={formData.adminFullName}
              onChange={handleInputChange('adminFullName')}
              fullWidth
              placeholder="John Doe"
              variant="outlined"
              error={!!formErrors.adminFullName}
              helperText={formErrors.adminFullName}
              required
              InputProps={{
                startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            
            
            <TextField
              label="Password"
              value={formData.password}
              onChange={handleInputChange('password')}
              fullWidth
              placeholder="Enter your password"
              variant="outlined"
              type={showPassword ? 'text' : 'password'}
              error={!!formErrors.password}
              helperText={formErrors.password}
              required
              InputProps={{
                startAdornment: <VpnKey sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isSubmitting}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password policy indicator (shared component) */}
            <PasswordPolicyIndicator
              validation={passwordValidation}
              show={!!formData.password}
            />

            <TextField
              label="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              fullWidth
              placeholder="Confirm your password"
              variant="outlined"
              type={showConfirmPassword ? 'text' : 'password'}
              error={
                !!formErrors.confirmPassword ||
                (formData.confirmPassword.length > 0 && !passwordsMatch)
              }
              helperText={formErrors.confirmPassword}
              required
              InputProps={{
                startAdornment: <VpnKey sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      disabled={isSubmitting}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password match indicator (shared component) */}
            <PasswordMatchIndicator
              match={passwordsMatch}
              show={!!formData.confirmPassword}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={
                isSubmitting ||
                !isPasswordValid(passwordValidation) ||
                !passwordsMatch ||
                !formData.confirmPassword
              }
              endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
              sx={{ 
                mt: 2,
                py: 1.5,
                textTransform: 'none',
                fontSize: '1.1rem',
                minHeight: '48px',
              }}
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </Button>

            {/* Sign in link */}
            <Box sx={{ textAlign: 'center'}}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link 
                  to="/login" 
                  style={{ 
                    color: muiTheme.palette.primary.main, 
                    textDecoration: 'none',
                    fontWeight: 500
                  }}
                >
                  Sign in here
                </Link>
              </Typography>
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              By creating an account, you agree to our Terms of Service and Privacy Policy
            </Typography>
          </Box>
        </Paper>

      {/* Copyright below form (Accsell-style footer) */}
      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{ mt: 3 }}
      >
        © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
      </Typography>
      </Container>
    </Box>
  );
};

export default CompanySignup;
